<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.discountsTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.discountsSubtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0">
          <router-link
            :to="{ name: 'CreateDiscount' }"
            class="btn btn-warning px-4 py-2 font-weight-bold text-dark"
          >
            <i class="mdi mdi-plus mr-1"></i>{{ $t('superadmin.createDiscount') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card">
        <div class="card-body">
          <!-- Search Bar -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div class="search-box mb-2 mb-md-0" style="min-width: 320px; max-width: 440px; width: 100%;">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('common.search') + ' code, title...'"
                />
                <button v-if="search" class="btn btn-outline-secondary" type="button" @click="search = ''">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <strong class="text-white">{{ filteredItems.length }}</strong>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover text-white align-middle">
              <thead>
                <tr class="text-muted text-uppercase small" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <th style="width: 45px;">No</th>
                  <th style="width: 150px;">{{ $t('superadmin.discountCode') }}</th>
                  <th>{{ $t('common.name') }}</th>
                  <th style="width: 130px;">{{ $t('superadmin.discountValue') }}</th>
                  <th style="width: 110px;">{{ $t('superadmin.usageLimit') }}</th>
                  <th style="width: 130px;">{{ $t('superadmin.expiryDate') }}</th>
                  <th class="text-right" style="width: 180px;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in paginatedDiscount"
                  :key="data.id"
                  style="border-bottom: 1px solid rgba(255,255,255,0.05);"
                >
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <span class="badge badge-outline-warning font-weight-bold px-3 py-1 font-monospace" style="letter-spacing: 1px;">
                      {{ data.code || '-' }}
                    </span>
                  </td>
                  <td>
                    <router-link
                      :to="{ name: 'DetailDiscount', params: { id: data.id } }"
                      class="font-weight-bold text-white text-decoration-none"
                    >
                      {{ data.title || '-' }}
                    </router-link>
                    <small class="text-muted text-truncate d-block" style="max-width: 250px;">
                      {{ data.desc || '-' }}
                    </small>
                  </td>
                  <td>
                    <span v-if="data.type === 'percent'" class="badge badge-success px-2 py-1">
                      {{ data.value }}% OFF
                    </span>
                    <span v-else class="badge badge-info px-2 py-1">
                      Rp {{ formatPrice(data.value) }} OFF
                    </span>
                  </td>
                  <td>
                    <span class="text-light">
                      <i class="mdi mdi-account-multiple mr-1 text-muted"></i>
                      {{ data.limit_user || 'Unlimited' }}
                    </span>
                  </td>
                  <td class="text-muted small">
                    <span :class="isExpired(data.expired_at) ? 'text-danger' : 'text-light'">
                      {{ formatDate(data.expired_at) }}
                    </span>
                  </td>
                  <td class="text-right text-nowrap">
                    <router-link
                      :to="{ name: 'DetailDiscount', params: { id: data.id } }"
                      class="btn btn-sm btn-secondary mr-1"
                      :title="$t('common.view')"
                    >
                      <i class="mdi mdi-eye mr-1"></i>{{ $t('common.view') }}
                    </router-link>
                    <router-link
                      :to="{ name: 'Editdiscont', params: { id: data.id } }"
                      class="btn btn-sm btn-outline-secondary mr-1"
                      :title="$t('common.edit')"
                    >
                      <i class="mdi mdi-pencil mr-1"></i>{{ $t('common.edit') }}
                    </router-link>
                    <button
                      @click="handleDelete(data.id)"
                      class="btn btn-sm btn-outline-danger"
                      :title="$t('common.delete')"
                    >
                      <i class="mdi mdi-delete-outline mr-1"></i>{{ $t('common.delete') }}
                    </button>
                  </td>
                </tr>

                <tr v-if="paginatedDiscount.length === 0">
                  <td colspan="7" class="text-center py-5">
                    <EmptyState
                      icon="mdi-ticket-percent-outline"
                      :title="$t('superadmin.noDiscountsFound')"
                      :description="$t('superadmin.noDiscountsDesc')"
                      :actionText="$t('superadmin.createPromoCode')"
                      actionTo="/discount/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredItems.length > itemsPerPage" class="mt-4">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDiscount, deleteDiscount } from '@/services/discountServices';
import moment from 'moment';
import PaginationComponents from '@/components/PaginationComponents.vue';
import EmptyState from '@/components/EmptyState.vue';
import Swal from 'sweetalert2';

export default {
  name: 'DiscountListView',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: '',
      discounts: [],
      currentPage: 1,
      itemsPerPage: 15,
      loading: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.discounts;
      const q = this.search.toLowerCase();
      return this.discounts.filter((item) => {
        const titleMatch = (item.title || '').toLowerCase().includes(q);
        const codeMatch = (item.code || '').toLowerCase().includes(q);
        const descMatch = (item.desc || '').toLowerCase().includes(q);
        return titleMatch || codeMatch || descMatch;
      });
    },
    paginatedDiscount() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const resp = await getDiscount();
        this.discounts = resp.data?.data || resp.data || [];
      } catch (err) {
        console.error('Error fetching discounts:', err);
      } finally {
        this.loading = false;
      }
    },
    formatPrice(val) {
      if (!val) return '0';
      return parseInt(val).toLocaleString('id-ID');
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
    isExpired(date) {
      if (!date) return false;
      return moment(date).isBefore(moment());
    },
    async handleDelete(id) {
      const result = await Swal.fire({
        title: this.$t('superadmin.confirmDeleteDiscountTitle'),
        text: this.$t('superadmin.confirmDeleteDiscountText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      });

      if (result.isConfirmed) {
        try {
          await deleteDiscount(id);
          Swal.fire({
            icon: 'success',
            title: this.$t('common.success'),
            text: this.$t('superadmin.discountDeletedSuccess'),
            confirmButtonColor: '#ffab00',
          });
          this.fetchData();
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: this.$t('common.error'),
            text: this.$currentLocale === 'id' ? 'Gagal menghapus voucher diskon.' : 'Failed to delete discount voucher.',
            confirmButtonColor: '#ffab00',
          });
        }
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>
